<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts" generic="T extends MedicalCondition">
import { reactive, watch, computed, toRef, Ref } from "vue";
import { helpers, required, requiredIf, minValue, maxValue } from "@vuelidate/validators";
import { ValidationArgs, ValidationRuleCollection } from "@vuelidate/core";
import { subYears } from "date-fns";

import { Input, Question, DatePicker, RadioSet, Radio, Select } from "@/components/fields";
import { MedicalCondition, PersonData } from "@/entities/conditions";
import { numbersArray, getNumberOptions, formatOptions } from "@/utils";
import { SchemaType, SchemaFormQuestion, SchemaQuestion, SchemaHtmlQuestion } from "./schema";
import Medication from "./Medication/Medication.vue";
import ValidationError from "../fields/ValidationError.vue";
import MedicalDiagnosisDateConflictModal from "./MedicalDiagnosisDateConflictModal.vue";
import evalDependancyRule from "@/utils/evalDependancyRule";

export interface Props<T> {
    schema: SchemaType,
    initialForm: T,
    diagnosisDate: string | null,
    personDetails?: PersonData
}

const props = defineProps<Props<T>>();

const emit = defineEmits<{
        (on:"update:form", value: T): void
    }>();

const evaluateWithContext = (answers: T, rule: string) => {
    rule = rule.replace("mNameIs", "name");
    return evalDependancyRule(rule, answers);
};

type Key = keyof MedicalCondition;
const refFormProp = toRef(props, "initialForm");

const state = reactive<{
    form: Ref<T>,
    hasDiagnosisDateChanged: boolean,
    prevDiagnosisDate: string | null,
}>({
    form: refFormProp.value,
    hasDiagnosisDateChanged: false,
    prevDiagnosisDate: null,
});

const accumValidationRules = (
    rules: ValidationRuleCollection,
    currentRule: string,
    config: SchemaFormQuestion,
) => {
    const ruleState = currentRule.split(":");
    switch (ruleState[0]) {
        case "required":
            if (config.mdependsOn) {
                rules = {
                    ...rules,
                    requiredIf: helpers.withMessage(`The ${config.alias} field is required.`,
                        requiredIf(() => evaluateWithContext(state.form, config.mdependsOn || ""))),
                };
            } else {
                rules = {
                    ...rules,
                    required: helpers.withMessage(`The ${config.alias} field is required.`, required),
                };
            }
            break;
        case "min_value":
            rules.minValue = helpers.withMessage(`The number of ${config.alias} field  must be ${ruleState[1]} or more.`, minValue(ruleState[1]));
            break;
        case "max_value":
            rules.maxValue = helpers.withMessage(`The number of ${config.alias} field  must be ${ruleState[1]} or less.`, maxValue(ruleState[1]));
            break;
        case "atLeastOneDay":
            rules = {
                ...rules,
                requiredIf: helpers.withMessage(`${config.alias} should be at least one day.`,
                    requiredIf(() => evaluateWithContext(state.form, config.mdependsOn || ""))),
            };
            break;
        case "integer":
            rules.integer = helpers.withMessage(`The ${config.alias} field must be a whole number.`, helpers.regex(/^\d+$/));
            break;
    }

    return rules;
};

const validation = computed(() => {
    const validation:{
        [key in Key]?: ValidationArgs
    } = {};

    props.schema.questions.forEach((config) => {
        if (!("validate" in config)) {
            return;
        }

        let rulesList = config.validate.split("|");
        if ("validateSpecial" in config) {
            if (config.validateSpecial) {
                const special = config.validateSpecial.find(spec => {
                    const [, field, value] = spec;
                    return state.form[field as Key] === value;
                });
                rulesList = special?.[3].split("|") || [];
            }
        }
        const rules: ValidationRuleCollection = rulesList.reduce((result, currentRule) => {
            return accumValidationRules(result, currentRule, config);
        }, {});
        validation[config.id as Key] = rules;
    },
    );

    return validation;
});

watch(state, () => {
    for (const question of props.schema.questions) {
        if (!checkVisibility(question) && state.form[question.id]) {
            state.form[question.id] = null;
        }
    }
    emit("update:form", state.form as T);
});
watch(refFormProp, () => {
    state.form = refFormProp.value;
});
watch(() => state.form.diagnosis_date, (curValue, prevValue) => {
    if (
        "medications" in state.form && state.form.medications.length &&
            state.prevDiagnosisDate !== curValue &&
            prevValue
    ) {
        state.hasDiagnosisDateChanged = true;
        state.prevDiagnosisDate = prevValue as unknown as string;
    }
}, { immediate: true });

const handleUndoDateChange = () => {
    state.hasDiagnosisDateChanged = false;
    state.form.diagnosis_date = state.prevDiagnosisDate;
    emit("update:form", state.form);
};

const handleRemoveMedications = () => {
    state.hasDiagnosisDateChanged = false;
    state.form.medications = [];
    emit("update:form", state.form);
};

const checkHtmlType = (question: SchemaQuestion) => {
    return (question.type === "html") &&
                !question.id.includes("_error") &&
                (question.mdependsOn && !question.mdependsOn.includes("this.")
                    ? evaluateWithContext(state.form, question.mdependsOn || "")
                    : true);
};

const checkVisibility = (question: SchemaQuestion) => {
    if (question.mdependsOn && !question.mdependsOn.includes("this.")) {
        return evaluateWithContext(state.form, question.mdependsOn || "");
    } else {
        return true;
    }
};

const getMaxDate = (question: Extract<SchemaQuestion, {type: "dateRow"}>) => {
    // json minAge is the max date
    const years = question.minAge && !Number.isNaN(Number(question.minAge))
        ? Number(question.minAge)
        : 0;

    return subYears(new Date(), years);
};

const getMinDate = (question: Extract<SchemaQuestion, {type: "dateRow"}>) => {
    const generateMaxDateForDiagnosisDate = () => {
        if (props.personDetails?.dob) {
            return new Date(props.personDetails.dob);
        }

        return subYears(new Date(), 100);
    };
    // json maxAge is the min date
    const maxAgeDate = props.diagnosisDate && question.id !== "diagnosis_date"
        ? new Date(props.diagnosisDate)
        : generateMaxDateForDiagnosisDate();

    if (!question.maxAge) { return maxAgeDate; }

    const [, holder, key, ...nested] = question.maxAge.split(".");

    if (nested.length) {
        throw new Error("Nested maxAge is not supported");
    }

    let date: unknown;

    if (holder === "diagnosisDate") {
        date = props.diagnosisDate;
    } else {
        date = state.form[key];
    }

    return date && typeof date === "string" ? new Date(date) : maxAgeDate;
};

const getFormValue = (slug: string) : string | number | null => {
    const value = state.form[slug];
    if (typeof value === "string" || typeof value === "number") {
        return value;
    }
    return null;
};

const getFormValueAsString = (slug: string) : string | null => {
    const val = getFormValue(slug);
    if (typeof val === "number") {
        return val.toString();
    }
    return val;
};

</script>
<template>
    <div
        v-for="(q, index) in schema.questions"
        :key="`${q.type}${index}`"
    >
        <template v-if="checkVisibility(q)">
            <div
                v-if="checkHtmlType(q)"
            >
                <div
                    v-for="(content, i) in (q as SchemaHtmlQuestion).content"
                    :key="`${content.type}${i}`"
                >
                    <div
                        v-if="content.type === 'p'"
                    >
                        <p
                            :class="content.class"
                            v-html="content.string"
                        />
                    </div>
                    <span
                        v-else-if="content.type === 'span'"
                        :class="content.class"
                    >
                        {{ content.string }}
                    </span>
                    <hr v-else-if="content.type === 'hr'">
                    <hr
                        v-else-if="content.type === 'hrDot'"
                        style="border-top: dotted 1px;"
                    >
                    <div v-else>
                        Unimplemented: {{ content }}
                    </div>
                </div>
            </div>
            <ValidationError
                v-else-if="q.type === 'validation'"
                name="error"
                :class="q.content[0].class"
                :validation="validation[q.id as Key]"
                :values="{}"
            />
            <div v-else-if="q.type === 'input'">
                <Question
                    :name="q.id"
                    :label="q.question"
                    :tooltip="q.tooltip"
                >
                    <Input
                        :id="q.id"
                        :key="q.validateSpecial ? JSON.stringify(validation[q.id as Key]) : q.id"
                        :model-value="getFormValue(q.id)"
                        :type="q.inp_type ? q.inp_type : 'text'"
                        :inputmode="q.inp_type === 'number' ? 'numeric' : 'text'"
                        :validation="validation[q.id as Key]"
                        @update:model-value="newValue => state.form[q.id] = newValue"
                    />
                </Question>
            </div>
            <div v-else-if="q.type === 'medication'">
                <Medication
                    v-model="state.form.medications"
                    :diagnosis-date="props.diagnosisDate"
                />
            </div>
            <div v-else-if="q.type === 'select'">
                <Question
                    :name="q.id"
                    :label="q.question"
                    :tooltip="q.tooltip"
                >
                    <Select
                        :id="q.id"
                        :model-value="getFormValue(q.id)"
                        placeholder="Please select"
                        :options="formatOptions(q.options) || numbersArray(q.to, q.from).map(getNumberOptions)"
                        :validation="validation[q.id as Key]"
                        @update:model-value="newValue => state.form[q.id] = newValue"
                    />
                </Question>
            </div>
            <div v-else-if="q.type === 'dateRow'">
                <Question
                    :name="q.id"
                    :label="q.question"
                    :tooltip="q.tooltip"
                >
                    <DatePicker
                        :id="q.id"
                        :model-value="getFormValueAsString(q.id)"
                        :max-date="getMaxDate(q)"
                        :min-date="getMinDate(q)"
                        :show-day-field="q.inp_type.includes('day')"
                        :validation="validation[q.id as Key]"
                        @update:model-value="newValue => state.form[q.id] = newValue"
                    />
                </Question>
            </div>
            <div v-else-if="q.type === 'readings'">
                <slot :name="q.id" />
            </div>
            <div v-else-if="q.type === 'radio'">
                <Question
                    :name="q.id"
                    :label="q.question"
                    :tooltip="q.tooltip"
                >
                    <RadioSet>
                        <Radio
                            v-for="opt in q.options"
                            :key="opt[1]"
                            :model-value="getFormValue(q.id)"
                            :title="opt[0]"
                            :value="opt[1]"
                            :validation="validation[q.id as Key]"
                            @update:model-value="newValue => state.form[q.id] = newValue"
                        />
                    </RadioSet>
                </Question>
            </div>
        </template>
    </div>
    <MedicalDiagnosisDateConflictModal
        :handle-undo-date-change="handleUndoDateChange"
        :has-diagnosis-date-changed="state.hasDiagnosisDateChanged"
        :handle-remove-medications="handleRemoveMedications"
    />
</template>
<style scoped>
.read-the-docs {
  color: #888;
}
</style>
